/**
 * Element highlighter configuration options
 */
export interface ElementHighlighterConfig {
  readonly enabled?: boolean;
  readonly highlightClassName?: string;
  readonly debugMode?: boolean;
}

/**
 * Element highlighter state interface
 */
interface ElementHighlighterState {
  readonly enabled: boolean;
  readonly currentElement: HTMLElement | null;
  readonly isHighlighting: boolean;
}

/**
 * ElementHighlighter API for highlighting HTML elements under cursor
 */
export interface ElementHighlighterAPI {
  readonly setEnabled: (enabled: boolean) => void;
  readonly getCurrentElement: () => HTMLElement | null;
  readonly update: (x: number, y: number, scale?: number) => void;
  readonly getState: () => Readonly<ElementHighlighterState>;
  readonly destroy: () => void;
}

/**
 * Creates an ElementHighlighter for highlighting HTML elements under cursor
 * @param container - Container element for the highlight overlay
 * @param config - Element highlighter configuration options
 * @returns ElementHighlighter API
 *
 * @example
 * ```typescript
 * const highlighter = createElementHighlighter(container, {
 *   enabled: true,
 *   debugMode: false
 * });
 *
 * // Update on cursor movement
 * highlighter.update(mouseX, mouseY);
 *
 * // Clean up
 * highlighter.destroy();
 * ```
 */
export const createElementHighlighter = (
  container: HTMLElement,
  config: ElementHighlighterConfig = {}
): ElementHighlighterAPI => {
  let isDestroyed = false;

  // Configuration with defaults
  const highlighterConfig = {
    enabled: config.enabled !== false, // Default to true
    highlightClassName:
      config.highlightClassName || "ui-helpers__element-highlight",
    debugMode: config.debugMode || false,
  };

  // Element highlighter state
  let state: ElementHighlighterState = {
    enabled: highlighterConfig.enabled,
    currentElement: null,
    isHighlighting: false,
  };

  // DOM elements for highlight
  let highlightDiv: HTMLElement | null = null;

  /**
   * Create highlight overlay div
   */
  const createHighlightDiv = (): void => {
    if (highlightDiv) return; // Already created

    highlightDiv = document.createElement("div");
    highlightDiv.className = highlighterConfig.highlightClassName;

    // Add to container
    container.appendChild(highlightDiv);
  };

  /**
   * Check if element is within HTMLRender layer
   */
  const isElementInHTMLRenderLayer = (element: HTMLElement): boolean => {
    // Look for HTMLRender layer container in the element's ancestors
    let current = element;
    while (current && current !== document.body) {
      if (
        current.classList.contains("stage__html-render") ||
        current.closest(".stage__html-render")
      ) {
        return true;
      }
      current = current.parentElement as HTMLElement;
    }
    return false;
  };

  /**
   * Get element under cursor position within the product container only
   */
  const getElementUnderCursor = (x: number, y: number): HTMLElement | null => {
    // Find the product container
    const productContainer = document.querySelector(
      ".stage__product-container"
    ) as HTMLElement;
    if (!productContainer) {
      return null;
    }

    // Check if cursor is within product container bounds
    const productRect = productContainer.getBoundingClientRect();
    if (
      x < productRect.left ||
      x > productRect.right ||
      y < productRect.top ||
      y > productRect.bottom
    ) {
      return null;
    }

    // Convert screen coordinates to coordinates relative to product container
    const relativeX = x - productRect.left;
    const relativeY = y - productRect.top;

    // Temporarily hide our highlight div to avoid interference
    const originalHighlightDisplay = highlightDiv?.style.display;
    if (highlightDiv) {
      highlightDiv.style.display = "none";
    }

    // Find the deepest element at the relative position within the product container
    const elementAtPosition = findElementAtPosition(
      productContainer,
      relativeX,
      relativeY
    );

    // Restore highlight display
    if (highlightDiv && originalHighlightDisplay !== undefined) {
      highlightDiv.style.display = originalHighlightDisplay;
    }

    return elementAtPosition;
  };

  /**
   * Recursively find the deepest element at a given position within a container
   */
  const findElementAtPosition = (
    container: HTMLElement,
    x: number,
    y: number
  ): HTMLElement | null => {
    const children = Array.from(container.children) as HTMLElement[];

    // Check each child element
    for (const child of children) {
      const childRect = child.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Convert child rect to coordinates relative to container
      const childLeft = childRect.left - containerRect.left;
      const childTop = childRect.top - containerRect.top;
      const childRight = childLeft + childRect.width;
      const childBottom = childTop + childRect.height;

      // Check if point is within this child
      if (
        x >= childLeft &&
        x <= childRight &&
        y >= childTop &&
        y <= childBottom
      ) {
        // Recursively check if there's a deeper child at this position
        const deeperChild = findElementAtPosition(
          child,
          x - childLeft,
          y - childTop
        );
        return deeperChild || child; // Return deeper child or this child
      }
    }

    // If no child contains the point, return null
    return null;
  };

  /**
   * Scale border radius value by dividing by scale factor
   * @param borderRadiusValue - CSS border-radius value (e.g., "8px", "4px 8px")
   * @param scale - Current scale factor
   * @returns Scaled border-radius CSS value
   */
  const scaleBorderRadius = (
    borderRadiusValue: string,
    scale: number
  ): string => {
    if (
      !borderRadiusValue ||
      borderRadiusValue === "0px" ||
      borderRadiusValue === "0"
    ) {
      return "0px";
    }

    // Handle multiple values (e.g., "4px 8px" or "4px 8px 12px 16px")
    const values = borderRadiusValue.split(/\s+/);

    const scaledValues = values.map((value) => {
      // Extract numeric value and unit (e.g., "8px" -> 8, "px")
      const match = value.match(/^(\d*\.?\d+)(px|rem|em|%)?$/);
      if (!match) return value; // Return as-is if can't parse

      if (!match[1]) return value;
      const numericValue = parseFloat(match[1]);
      const unit = match[2] || "px";

      // Only scale px values (other units like % should remain unchanged)
      if (unit === "px") {
        const scaledValue = numericValue * scale;
        return `${scaledValue.toFixed(2)}px`;
      }

      return value; // Return unchanged for non-px units
    });

    return scaledValues.join(" ");
  };

  /**
   * Update highlight position to match target element
   */
  const updateHighlight = (
    targetElement: HTMLElement,
    scale: number = 1
  ): void => {
    if (!highlightDiv) {
      console.log("createHighlightDiv");
      createHighlightDiv();
    }

    if (!highlightDiv) return;

    const targetRect = targetElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate element position relative to container
    const elementLeft = targetRect.left - containerRect.left;
    const elementTop = targetRect.top - containerRect.top;
    const elementWidth = targetRect.width;
    const elementHeight = targetRect.height;

    highlightDiv.className = highlighterConfig.highlightClassName;

    // Position highlight div to match target element exactly
    highlightDiv.style.display = "block";
    highlightDiv.style.left = elementLeft + "px";
    highlightDiv.style.top = elementTop + "px";
    highlightDiv.style.width = elementWidth + "px";
    highlightDiv.style.height = elementHeight + "px";
    highlightDiv.style.position = "absolute";
    // Get computed styles to match target element's border radius
    const computedStyles = window.getComputedStyle(targetElement);
    // Scale the border radius to match the visual appearance at current scale
    const scaledBorderRadius = scaleBorderRadius(
      computedStyles.borderRadius || "0px",
      scale
    );
    highlightDiv.style.borderRadius = scaledBorderRadius;
  };

  /**
   * Hide highlight
   */
  const hideHighlight = (): void => {
    if (highlightDiv) {
      highlightDiv.style.display = "none";
    }
  };

  /**
   * Set highlighter enabled state
   */
  const setEnabled = (enabled: boolean): void => {
    if (isDestroyed) return;

    state = { ...state, enabled };

    if (!enabled) {
      hideHighlight();
      state = { ...state, currentElement: null, isHighlighting: false };
    }
  };

  /**
   * Get current highlighted element
   */
  const getCurrentElement = (): HTMLElement | null => {
    return state.currentElement;
  };

  /**
   * Update highlighter with cursor position
   */
  const update = (x: number, y: number, scale: number = 1): void => {
    if (isDestroyed || !state.enabled) return;

    const elementUnderCursor = getElementUnderCursor(x, y);

    // Debug logging when debug mode is enabled
    if (highlighterConfig.debugMode) {
      console.log("🎯 ElementHighlighter debug:", {
        coordinates: { x, y },
        finalElement: elementUnderCursor?.tagName,
        finalClasses: elementUnderCursor?.className,
        enabled: state.enabled,
        isDestroyed: isDestroyed,
      });
    }
    // Check if element changed
    if (elementUnderCursor !== state.currentElement) {
      state = { ...state, currentElement: elementUnderCursor };

      if (elementUnderCursor) {
        updateHighlight(elementUnderCursor, scale);
        state = { ...state, isHighlighting: true };
      } else {
        hideHighlight();
        state = { ...state, isHighlighting: false };
      }
    }
  };

  /**
   * Get current state
   */
  const getState = (): Readonly<ElementHighlighterState> => ({ ...state });

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    if (highlightDiv && highlightDiv.parentNode) {
      highlightDiv.parentNode.removeChild(highlightDiv);
    }

    highlightDiv = null;
  };

  // Initialize highlight div if enabled
  if (state.enabled) {
    createHighlightDiv();
  }

  return {
    setEnabled,
    getCurrentElement,
    update,
    getState,
    destroy,
  };
};
